import _ from "lodash";
import React from "react";
import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";
import PropTypes from "prop-types";
import { useI18n } from "Hooks";

import PAErrorComponent from "./PAErrorComponent";
import { getHOCDisplayName } from "Utils";
import {
  MODULES,
  RULE_OPTIONS,
  getAccessLevels,
  getIsAdminByRole,
} from "lib/staffPermissionUtils";

// a custom blockAccessFunction can be passed which receives hasPermissionBasedAccess and hasModuleLevelAccess as args
// by default we check if the user has access to both permissions and module
const blockAccessFunctionFallback = ({
  hasPermissionBasedAccess,
  hasModuleLevelAccess,
}) => {
  return !hasPermissionBasedAccess || !hasModuleLevelAccess;
};

const contextPropType = {
  entityId: PropTypes.string,
  entityType: PropTypes.string,
};

const controlParameterPropType = {
  type: PropTypes.oneOf(["MODULE_SETTINGS", "PERMISSION"]),
  controlParameter: PropTypes.string,
  contexts: PropTypes.arrayOf(PropTypes.shape(contextPropType)),
};

// prop types and their brief description
const accessBlockerProps = {
  /*
    for permission aware error component
    ===========================================================================================================
      errorImageSrc: illustration shown on error component
      errorText: the title text under the illustration, this is overriden if user does not have module level access
      errorSubText: sub text for the illustration
      redirectButtonText: custom text instead of "Go to homepage"
      onRedirectButtonClick: custom functionality insteade of goToHomepage
  */
  errorImageSrc: PropTypes.string,
  errorText: PropTypes.string,
  errorSubText: PropTypes.string,
  redirectButtonText: PropTypes.string,
  onRedirectButtonClick: PropTypes.func,

  /*
    for permission based access checks
    ===========================================================================================================
      accessPermissions: either a permission or an array of permissions to validate
      permissionsToCheck: should validate any "one" permission or "all" permissions
        
    * permission checks are skipped if "accessPermissions" prop is not passed
  */
  accessPermissions: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
  ]),
  permissionsToCheck: PropTypes.oneOf(["one", "all"]),

  /*
    for module role based access (based on RnPAccessBlocker)
    ===========================================================================================================
      moduleId: id of the module we are checking access for
      moduleLocale: used for errorText, "you dont have access to {module}"
      customModuleRule: rule for checking module access, defaults to block if less than admin level

    * module level checks are skipped if "moduleId" prop is not passed
  */
  moduleId: PropTypes.string,
  moduleLocale: PropTypes.string,
  customModuleRule: PropTypes.object,

  /*
    props for changing behaviour
    ===========================================================================================================
      showErrorComp: should render PAErrorComponent or not, default: true
      isAccessCheckSkipped: skip checking for access but render the wrapped component, default: false
      isAccessBlocked: skip checking for access and render error component, default: false, * this is overriden by isAccessCheckSkipped
      blockAccessFunction: custom function to block access when it returns true, receives hasPermissionBasedAccess and hasModuleLevelAccess as args
  */
  showErrorComp: PropTypes.bool,
  isAccessBlocked: PropTypes.bool,
  isAccessCheckSkipped: PropTypes.bool,
  blockAccessFunction: PropTypes.func,

  /**
   * Props for Role based permissions
   * ===========================================================================================================
   */
  controlParameterObj: PropTypes.shape(controlParameterPropType),
};

//Question: Should we use this HOC for Role based permissions or create new?
function CombinedAccessBlockerHOC(WrappedComponent) {
  const AccessBlocker = props => {
    const {
      errorText,
      errorSubText,
      errorImageSrc,
      redirectButtonText,
      onRedirectButtonClick,

      accessPermissions,
      permissionsToCheck,

      moduleId,
      moduleLabel,
      customModuleRule,

      showErrorComp,
      isAccessBlocked,
      isAccessCheckSkipped,
      blockAccessFunction,

      controlParameterObj,
    } = props;

    const { t } = useI18n(["common"]);

    // skip all checks if this is true
    if (isAccessCheckSkipped) return <WrappedComponent {...props} />;

    let hasPermissionBasedAccess,
      hasModuleLevelAccess,
      updatedErrorText = errorText;

    const errorComponent = showErrorComp ? (
      <PAErrorComponent
        errorText={updatedErrorText}
        errorSubText={errorSubText}
        errorImageSrc={errorImageSrc}
        redirectButtonText={redirectButtonText}
        onRedirectButtonClick={onRedirectButtonClick}
      />
    ) : null;

    // skip all checks and render error component
    if (isAccessBlocked) {
      return errorComponent;
    }

    const isSchoolAdmin = getIsAdminByRole();

    /**
     * Here, controlParameterObj will replace both staff Permission and
     * configurations based checks eventually with role based permissions or feature flags.
     * So, if this parameter is present then we will skip both staff permission and configurations based checks.
     *
     * TODO: RNP CLEANUP TASK: REMOVE if condition and cleanup HOC, after RnP is launched.
     */
    if (_.isUndefined(controlParameterObj)) {
      if (moduleId && _.has(MODULES, moduleId) && !isSchoolAdmin) {
        const module = MODULES[moduleId];

        // If the custom module rule is not provided, then it checks if the user has admin access or not
        const defaultNonAdminAccessRule = {
          rule: RULE_OPTIONS.lt,
          value: module.values.ADMIN_ACCESS.id,
          defaultAccess: false,
        };

        const rule = customModuleRule || defaultNonAdminAccessRule;

        const { blockModuleLevelAccess } = getAccessLevels({
          [moduleId]: {
            blockModuleLevelAccess: rule,
          },
        });

        updatedErrorText = t(
          "common:sorry_you_dont_have_access_to_with_module_name",
          {
            moduleName: moduleLabel,
          }
        );

        hasModuleLevelAccess = !blockModuleLevelAccess;
      } else hasModuleLevelAccess = true;

      if (!_.isEmpty(accessPermissions)) {
        hasPermissionBasedAccess = ACLStore.can(
          accessPermissions,
          permissionsToCheck
        );
      } else hasPermissionBasedAccess = true;
    } else {
      const { type, controlParameter, contexts } = controlParameterObj;

      let isRnPAccessEnabled = false;
      const isNestedContexts =
        _.isArray(contexts) && _.isArray(_.get(contexts, "[0]"));

      switch (type) {
        case "MODULE_SETTINGS": {
          isRnPAccessEnabled = !_.isUndefined(contexts)
            ? isNestedContexts
              ? _.some(contexts, contextArray =>
                  ACLStorage.getModuleSettingsValueByContext(
                    controlParameter,
                    contextArray
                  )
                )
              : ACLStorage.getModuleSettingsValueByContext(
                  controlParameter,
                  contexts
                )
            : ACLStorage.getModuleSettingsValue(controlParameter);
          break;
        }
        case "PERMISSION": {
          isRnPAccessEnabled = !_.isUndefined(contexts)
            ? isNestedContexts
              ? _.some(contexts, contextArray =>
                  ACLStorage.getPermissionValueByContext(
                    controlParameter,
                    contextArray
                  )
                )
              : ACLStorage.getPermissionValueByContext(
                  controlParameter,
                  contexts
                )
            : ACLStorage.getPermissionValue(controlParameter);
          break;
        }

        default: {
          isRnPAccessEnabled = false;
        }
      }

      hasPermissionBasedAccess = hasModuleLevelAccess = isRnPAccessEnabled;

      if (!isRnPAccessEnabled) {
        updatedErrorText = t(
          "common:sorry_you_dont_have_access_to_with_module_name",
          {
            moduleName: moduleLabel,
          }
        );
      }
    }

    return blockAccessFunction({
      hasPermissionBasedAccess,
      hasModuleLevelAccess,
    }) ? (
      errorComponent
    ) : (
      <WrappedComponent {...props} />
    );
  };

  AccessBlocker.displayName = `CombinedAccessBlocker(${getHOCDisplayName(
    WrappedComponent
  )})`;

  AccessBlocker.defaultProps = {
    accessPermissions: [],
    permissionsToCheck: "one",
    showErrorComp: true,
    isAccessBlocked: false,
    isAccessCheckSkipped: false,
    blockAccessFunction: blockAccessFunctionFallback,
  };

  AccessBlocker.propTypes = accessBlockerProps;

  return AccessBlocker;
}

export default CombinedAccessBlockerHOC;
